// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState } from 'react'
import type { CSSProperties } from 'react'
import { openTherapyDocument } from './therapyDocuments'

/**
 * The two patient-facing documents a therapy produces, on one control.
 *
 * WHY THIS IS SHARED (L-067): the enrollment PDF button had been pasted into three surfaces and
 * the consent into one, so "which documents can I see here?" depended on which screen you happened
 * to be on — the enrollment queue had both, the therapy lanes had one, and clinical review, the
 * stage that actually decides whether a therapy proceeds, had neither. Mounting one unit
 * everywhere means a document added later appears in every stage at once instead of two of them.
 *
 * Both open the CURRENT document: the signed instrument when one is filed, the watermarked draft
 * otherwise. Neither draft is for signature — the enrollment is gated on clinical review
 * completing, and the consent's real copy is the one the e-sign lane sends.
 *
 * The host passes its own `buttonStyle` so this fits the screen it sits on rather than importing a
 * fourth visual language into three existing ones.
 *
 * WHICH document each button opens is not decided here — therapyDocuments.openTherapyDocument is
 * the single opener every CareRx surface shares, so no screen can drift onto a different artifact.
 */
export default function TherapyDocumentButtons({
  therapyId,
  buttonStyle,
  onError,
}: {
  therapyId: string
  buttonStyle: CSSProperties
  /** Host-supplied error surface. Falls back to alert() when the host has none. */
  onError?: (message: string) => void
}) {
  const [busy, setBusy] = useState<'enrollment' | 'consent' | null>(null)

  const fail = (e: unknown, fallback: string) => {
    const m = e instanceof Error ? e.message : fallback
    if (onError) onError(m); else alert(m)
  }

  const enrollment = async () => {
    setBusy('enrollment')
    // The opener claims the tab inside this click gesture; a tab opened after the fetch resolves
    // is popup-blocked silently, which reads as "the button does nothing".
    try { await openTherapyDocument('enrollment', therapyId) }
    catch (e) { fail(e, 'Enrollment render failed') }
    finally { setBusy(null) }
  }

  const consent = async () => {
    setBusy('consent')
    // The opener claims the tab inside this click gesture; a tab opened after the fetch resolves
    // is popup-blocked silently, which reads as "the button does nothing".
    try { await openTherapyDocument('consent', therapyId) }
    catch (e) { fail(e, 'Consent render failed') }
    finally { setBusy(null) }
  }

  return (
    <>
      {/* D-CRX-80: the generated enrollment PDF — one renderer, so the lab table is sized to the
          drug record rather than fixed at three, and every surface shows the same document. */}
      <button style={buttonStyle} disabled={busy !== null} onClick={enrollment}
        title="The generated enrollment form — every required lab and the drug-record protocol, rendered server-side">
        {busy === 'enrollment' ? 'Building…' : 'Enrollment PDF'}
      </button>
      <button style={buttonStyle} disabled={busy !== null} onClick={consent}
        title="Draft Patient Consent for Services — generated from this pharmacy's own branding, address and licensing state">
        {busy === 'consent' ? 'Building…' : 'Consent PDF'}
      </button>
    </>
  )
}
